import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useMemo, useRef } from "react";
import {
    Animated,
    Easing,
    StyleSheet,
    TouchableOpacity,
    View,
    useWindowDimensions
} from "react-native";
import { Text, Icon } from "react-native-paper";

import { useResolvedReducedMotion } from "@/hooks/useResolvedReducedMotion";
import { colors, fonts, gradients, radius, spacing } from "@/lib/theme";

interface SubmissionCelebrationProps {
    firstName?: string | null;
    onDismiss: () => void;
}

const CONFETTI_COLORS = [
    colors.teal400,
    colors.teal300,
    colors.pink400,
    colors.pink300,
    colors.warning400,
    colors.success400
];

const PIECE_COUNT = 48;

interface PieceSpec {
    startX: number;
    size: number;
    color: string;
    round: boolean;
    delay: number;
    duration: number;
    swayAmplitude: number;
    spins: number;
}

/** One falling confetti piece, driven by a single 0→1 progress value. */
function ConfettiPiece({ spec, screenHeight }: { spec: PieceSpec; screenHeight: number }) {
    const progress = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(progress, {
            toValue: 1,
            duration: spec.duration,
            delay: spec.delay,
            easing: Easing.in(Easing.quad),
            useNativeDriver: true
        }).start();
    }, []);

    const translateY = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [-60, screenHeight + 60]
    });
    const translateX = progress.interpolate({
        inputRange: [0, 0.25, 0.5, 0.75, 1],
        outputRange: [0, spec.swayAmplitude, 0, -spec.swayAmplitude, 0]
    });
    const rotate = progress.interpolate({
        inputRange: [0, 1],
        outputRange: ["0deg", `${spec.spins * 360}deg`]
    });
    const opacity = progress.interpolate({
        inputRange: [0, 0.8, 1],
        outputRange: [1, 1, 0]
    });

    return (
        <Animated.View
            pointerEvents="none"
            style={{
                position: "absolute",
                top: 0,
                left: spec.startX,
                width: spec.size,
                height: spec.round ? spec.size : spec.size * 1.7,
                borderRadius: spec.round ? spec.size / 2 : 2,
                backgroundColor: spec.color,
                opacity,
                transform: [{ translateY }, { translateX }, { rotate }]
            }}
        />
    );
}

/**
 * Full-screen overlay shown ONCE, the moment the nurse submits the last
 * required onboarding item: confetti + "your application is under review".
 * Purely informational — dismisses to the hub, which shows the persistent
 * under-review card with the same explanation.
 */
export function SubmissionCelebration({ firstName, onDismiss }: SubmissionCelebrationProps) {
    const { width, height } = useWindowDimensions();
    const { resolved, reduced } = useResolvedReducedMotion();
    const scaleAnim = useRef(new Animated.Value(0)).current;
    const fadeAnim = useRef(new Animated.Value(0)).current;

    const pieces = useMemo<PieceSpec[]>(
        () =>
            Array.from({ length: PIECE_COUNT }, (_, i) => ({
                startX: Math.random() * width,
                size: 7 + Math.random() * 6,
                color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                round: i % 4 === 0,
                delay: Math.random() * 900,
                duration: 2600 + Math.random() * 1800,
                swayAmplitude: (12 + Math.random() * 36) * (Math.random() < 0.5 ? -1 : 1),
                spins: (2 + Math.random() * 2) * (Math.random() < 0.5 ? -1 : 1)
            })),
        [width]
    );

    useEffect(() => {
        Animated.sequence([
            Animated.spring(scaleAnim, {
                toValue: 1,
                friction: 4,
                tension: 60,
                useNativeDriver: true
            }),
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 400,
                useNativeDriver: true
            })
        ]).start();
    }, []);

    return (
        <View style={styles.container}>
            {resolved && !reduced && (
                <View style={StyleSheet.absoluteFill} pointerEvents="none">
                    {pieces.map((spec, i) => (
                        <ConfettiPiece key={i} spec={spec} screenHeight={height} />
                    ))}
                </View>
            )}

            <View style={styles.content}>
                <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={styles.checkCircle}
                    >
                        <Icon source="check-decagram" size={64} color={colors.surface} />
                    </LinearGradient>
                </Animated.View>

                <Animated.View style={{ opacity: fadeAnim }}>
                    <Text style={styles.title}>
                        {firstName ? `Amazing job, ${firstName}!` : "Amazing job!"}
                    </Text>
                    <Text style={styles.subtitle}>
                        You&apos;ve completed your onboarding application. Our team is now reviewing
                        it — we&apos;ll notify you the moment you&apos;re activated.
                    </Text>
                </Animated.View>

                <Animated.View style={[styles.buttonContainer, { opacity: fadeAnim }]}>
                    <TouchableOpacity onPress={onDismiss} activeOpacity={0.85}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.button}
                        >
                            <Text style={styles.buttonText}>Got it</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </Animated.View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        ...StyleSheet.absoluteFill,
        backgroundColor: colors.background,
        zIndex: 100,
        justifyContent: "center",
        alignItems: "center"
    },
    content: {
        alignItems: "center",
        paddingHorizontal: spacing.xl,
        gap: spacing.lg
    },
    checkCircle: {
        width: 120,
        height: 120,
        borderRadius: 60,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: colors.teal400,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.4,
        shadowRadius: 20,
        elevation: 10
    },
    title: {
        fontSize: 28,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        textAlign: "center",
        marginBottom: spacing.sm
    },
    subtitle: {
        fontSize: 16,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 24
    },
    buttonContainer: {
        width: "100%",
        marginTop: spacing.md
    },
    button: {
        alignItems: "center",
        justifyContent: "center",
        borderRadius: radius.lg,
        paddingVertical: 16,
        paddingHorizontal: spacing.xl
    },
    buttonText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.surface
    }
});
